import axios from 'axios';
// Importar tipos DO ARQUIVO DE TIPOS
import type { 
  ContractPaymentType, 
  ContractAdditiveMotive, 
  ContractOperationSuspended, 
  ContractReasonTermination, 
  // Importar também ContractFormData se for usar nos métodos create/update
  ContractFormData, 
  Contract 
} from '../types/contract';

// Remover definições locais dos tipos que foram movidos
// export interface ContractPaymentType { ... }
// export interface ContractAdditiveMotive { ... }
// export interface ContractOperationSuspended { ... }
// export interface ContractReasonTermination { ... }

// Usar variável de ambiente
const API_BASE_URL = `${import.meta.env.VITE_API_BASE_URL}/api`;

class ContractService {

  // Métodos usam os tipos importados
  async getAllPaymentTypes(): Promise<ContractPaymentType[]> {
    try {
      const response = await axios.get<ContractPaymentType[]>(`${API_BASE_URL}/contract-payment-types`);
      return response.data;
    } catch (error) {
      console.error("Erro ao buscar tipos de pagamento de contrato:", error);
      throw new Error('Falha ao buscar tipos de pagamento.');
    }
  }

  async getAllAdditiveMotives(): Promise<ContractAdditiveMotive[]> {
    try {
      const response = await axios.get<ContractAdditiveMotive[]>(`${API_BASE_URL}/contract-additive-motives`);
      return response.data;
    } catch (error) {
      console.error("Erro ao buscar motivos de aditivo de contrato:", error);
      throw new Error('Falha ao buscar motivos de aditivo.');
    }
  }

  async getAllOperationSuspended(): Promise<ContractOperationSuspended[]> {
    try {
      const response = await axios.get<ContractOperationSuspended[]>(`${API_BASE_URL}/contract-operation-suspended`);
      return response.data;
    } catch (error) {
      console.error("Erro ao buscar operações de suspensão de contrato:", error);
      throw new Error('Falha ao buscar operações de suspensão.');
    }
  }

  async getAllReasonTerminations(): Promise<ContractReasonTermination[]> {
    try {
      const response = await axios.get<ContractReasonTermination[]>(`${API_BASE_URL}/contract-reason-termination`);
      return response.data;
    } catch (error) {
      console.error("Erro ao buscar motivos de rescisão de contrato:", error);
      throw new Error('Falha ao buscar motivos de rescisão.');
    }
  }

  // --- Métodos para Contrato (Exemplos) --- 
  async getById(id: number): Promise<Contract> {
    try {
      const response = await axios.get<Contract>(`${API_BASE_URL}/contracts/${id}`);
      return response.data;
    } catch (error) {
      console.error(`Erro ao buscar contrato com ID ${id}:`, error);
      // Lançar o erro para que o componente possa tratá-lo
      throw error;
    }
  }
  
  async create(data: Partial<ContractFormData>): Promise<Contract> { 
    try {
      const response = await axios.post<Contract>(`${API_BASE_URL}/contracts`, data);
      return response.data;
    } catch (error) {
      console.error("Erro ao criar contrato:", error);
      // Lançar o erro para que o componente possa tratá-lo (ex: exibir mensagem)
      throw error; 
    }
  }

  async update(id: number, data: Partial<ContractFormData>): Promise<Contract> { 
    try {
      const response = await axios.put<Contract>(`${API_BASE_URL}/contracts/${id}`, data);
      return response.data;
    } catch (error) {
      console.error(`Erro ao atualizar contrato ${id}:`, error);
       // Lançar o erro
      throw error;
    }
  }

  // --- Método para buscar contratos com paginação/filtros/ordenação ---
  async getAll(params: any = {}): Promise<{ data: Contract[]; pagination: any }> {
    try {
      // Limpar parâmetros vazios para não poluir a URL
      Object.keys(params).forEach(key => {
        if (params[key] === '' || params[key] === null || params[key] === undefined || params[key] === 'Todos') {
          delete params[key];
        }
      });
      const response = await axios.get<{ data: Contract[]; pagination: any }>(`${API_BASE_URL}/contracts`, { params });
      return response.data;
    } catch (error) {
      console.error("Erro ao buscar contratos:", error);
      // Lançar o erro para que o componente de lista possa tratá-lo
      throw error;
    }
  }

  // --- Método para buscar estatísticas do Dashboard --- 
  async getDashboardStats(): Promise<{ 
    activeContracts: number, 
    noSuspensionLastYear: number, 
    noAdditiveLastYear: number, 
    equipmentsInUse: number,
    newContractsLast30Days: number,
    terminatedContractsLast30Days: number,
    suspendedThisMonth: number
  }> {
    try {
      const response = await axios.get<{ 
        activeContracts: number, 
        noSuspensionLastYear: number, 
        noAdditiveLastYear: number, 
        equipmentsInUse: number,
        newContractsLast30Days: number,
        terminatedContractsLast30Days: number,
        suspendedThisMonth: number 
      }>(`${API_BASE_URL}/contracts/dashboard-stats`);
      return response.data;
    } catch (error) {
      console.error("Erro ao buscar estatísticas do dashboard:", error);
      // Lançar um erro genérico ou o erro da API
      throw new Error('Falha ao buscar estatísticas do dashboard.');
    }
  }

  // Método para buscar contratos do dashboard por tipo
  async getDashboardContracts(type: string, page: number = 1, limit: number = 12) {
    try {
      const response = await axios.get(`${API_BASE_URL}/contracts/dashboard-contracts/${type}`, {
        params: { page, limit }
      });
      return response.data;
    } catch (error) {
      console.error("Erro ao buscar contratos do dashboard:", error);
      throw error;
    }
  }

  async removeSuspension(suspensionId: number): Promise<void> {
    await axios.delete(`${API_BASE_URL}/contracts/suspensions/${suspensionId}`);
  }

  // async delete(id: number): Promise<void> { ... }
}

export default new ContractService(); 